import React from 'react';
import { Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { blue20, blue50 } from '../../constants/colors';

/**
 * 子パターンバッジ（#2967）。
 *
 * OCR一覧・OCR詳細・ホームの処理履歴で、どの子パターンの取得ルールで読み取ったかを示す。
 *
 * 要注意バッジ（#3262）とは見た目を分ける。要注意は「人が確認すべき」という警告なので赤、
 * こちらは「どの設定で読んだか」という情報なので青にする（#3314）。
 * 子パターンで処理されるのは正常な状態であり、対応を促すものではない。
 * エラー（red50 / red20）と同じ「文字色 / 背景色」の組み合わせ方に揃える。
 */

interface SubPatternChipProps {
  /** 子パターンの名称。空なら何も描画しない */
  subPatternName: string | null | undefined;
  /** 余白の調整用 */
  marginLeft?: string;
  /**
   * 編集の起点にする場合に渡す（#2967）。
   * 渡すと鉛筆アイコンが付き、クリックできるようになる。
   * 未指定なら表示専用（結果一覧など、編集導線を持たない画面）
   */
  onEdit?: () => void;
}

export const SubPatternChip: React.FC<SubPatternChipProps> = ({
  subPatternName,
  marginLeft,
  onEdit,
}) => {
  if (!subPatternName) return null;

  // ツールチップは付けない。子パターン名が出ている時点で意味は伝わり、
  // 説明を足すと読む手間が増えるだけになるため
  return (
    <Chip
      label={subPatternName}
      size="small"
      variant="outlined"
      // 編集できるときだけアイコンを出す。担当者欄の鉛筆アイコンと同じ流儀にして、
      // 「クリックすると編集できる」ことが見た目から分かるようにする
      {...(onEdit
        ? {
            onClick: onEdit,
            deleteIcon: <EditIcon />,
            onDelete: onEdit,
          }
        : {})}
      sx={{
        // 青の文字・枠に薄い青の背景。MUI 既定の枠線は薄く地の文に紛れるため明示する
        backgroundColor: blue20,
        borderColor: blue50,
        color: blue50,
        maxWidth: '180px',
        flexShrink: 0,
        // 既定の 24px だとステータス行（行高 16px）に置いたとき行が膨らむため詰める
        height: '20px',
        // deleteIcon は既定でグレーになるため、文字と同じ色に揃える
        '& .MuiChip-deleteIcon': {
          color: blue50,
          fontSize: '14px',
          '&:hover': { color: blue50 },
        },
        ...(marginLeft ? { marginLeft } : {}),
      }}
    />
  );
};

export default SubPatternChip;
